import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Container,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    CircularProgress,
    Tooltip,
    Button, Dialog, DialogTitle, DialogContent, List, ListItem, LinearProgress, Select, MenuItem, FormControl, InputLabel, DialogActions, TextField, FormControlLabel, Switch,
    IconButton,
    Stack,
    Chip,
    Divider,
} from '@mui/material';
import PopupNotification from './PopupNotification';
import { useNotification } from './context/NotificationContext';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';


const BlueCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: 'rgb(87, 87, 172)' }}></div>
    </div>
);

const GreenCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: 'green' }}></div>
    </div>
);

const RedCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div className="flashing" style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: 'red' }}></div>
    </div>
);


const GreenOutlineCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
        <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#b0b0b0', border: '2px solid green', boxSizing: 'border-box' }}></div>
    </div>
);

const RedDashedOutlineCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div style={{
            width: '14px', height: '14px', borderRadius: '50%', border: '2px dashed red', boxSizing: 'border-box'
        }}></div>
    </div>
);


const GreenDashedOutlineCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div style={{
            width: '14px', height: '14px', borderRadius: '50%', border: '2px dashed green', boxSizing: 'border-box'
        }}></div>
    </div>
);

const RedOutlineCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
        <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#b0b0b0', border: '2px solid red', boxSizing: 'border-box' }}></div>
    </div>
);

const GreyOutlineCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '90%' }}>
        <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#b0b0b0', border: '2px solid white', boxSizing: 'border-box' }}></div>
    </div>
);

const GrayCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: 'grey' }}></div>
    </div>
);

const RedStopIcon = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div style={{ width: '14px', height: '14px', backgroundColor: 'darkred' }}></div>
    </div>
);


const GreenStopIcon = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div style={{ width: '14px', height: '14px', backgroundColor: 'green' }}></div>
    </div>
);



const GrayDashedOutlineCircle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <div style={{
            width: '14px',
            height: '14px',
            borderRadius: '50%',
            border: '2px dashed gray',
            boxSizing: 'border-box'
        }}></div>
    </div>
);


const darkTheme = createTheme({
    palette: {
        mode: 'dark',
        // You can override colors here or use the default dark palette
        background: {
            default: '#121212',
            paper: '#1e1e1e',
        },
        text: {
            primary: '#818589',
            secondary: '#aaaaaa',
        },
        // You can also adjust success, warning, etc. if needed
        success: {
            main: '#4caf50',
        },
        primary: {
            main: '#90caf9',
        },
    },
});

const EXPIRY_FETCH_COOLDOWN_MS = 1 * 60 * 1000;
const ACTIVE_GOIP_REFRESH_MS = 60 * 1000;
const DATA_POLL_MS = 3000;
const ROUTE_POLL_MS = 60 * 60 * 1000;
const MONITOR_7687_SIP_MIN_INTERVAL_MS = 60 * 60 * 1000;
const RECENT_SMS_POLL_MS = 60 * 60 * 1000;
const MONITOR_CALL_MIN_INTERVAL_MS = 10000;

const StatusCard = ({ label, value, icon, color, helper }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
        }}
    >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: color || '#fff', lineHeight: 1 }}>
                {value}
            </Typography>
            {helper ? (
                <Typography sx={{ fontSize: 9, color: 'rgba(255,255,255,0.45)' }}>
                    {helper}
                </Typography>
            ) : null}
        </Box>
    </Box>
);


const DataTable = () => {
    const { showNotification } = useNotification();
    const [data, setData] = useState({ goip1: [], goip2: [], goip3: [], goip4: [], goip5: [], goip6: [], goip7: [], goip8: [], goip9: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [expiryDataLoading, setExpiryDataLoading] = useState(true);
    const [smsStatusData, setSmsStatusData] = useState({ goip1: [], goip2: [], goip3: [], goip4: [], goip5: [], goip6: [], goip7: [], goip8: [], goip9: [] });
    const [, setTick] = useState(0);
    const [error, setError] = useState(null);
    const [callTimeBase, setCallTimeBase] = useState({ goip1: {}, goip2: {}, goip3: {}, goip4: {}, goip5: {}, goip6: {}, goip7: {}, goip8: {}, goip9: {} });
    const prevDataRef = useRef({ goip1: [], goip2: [], goip3: [], goip4: [], goip5: [], goip6: [], goip7: [], goip8: [], goip9: [] });
    const [showPopup, setShowPopup] = useState(false);
    const [fadeOut, setFadeOut] = useState(false);
    const [popupMessage, setPopupMessage] = useState('');
    const [smsSenders, setSmsSenders] = useState([]);
    const [recentSmsData, setRecentSmsData] = useState([]);
    const [smsData, setSmsData] = useState([]);
    // NEW: State to hold the list of active GoIPs
    const [activeGoips, setActiveGoips] = useState([]);
    const activeGoipsRef = useRef([]);
    const fetchCycleRunningRef = useRef(false);
    const lastMonitorCallFetchAtRef = useRef({});
    const lastMonitorSipFetchAtRef = useRef({});
    const isPageVisibleRef = useRef(typeof document === 'undefined' ? true : !document.hidden);
    const monitorCallQueueRef = useRef(Promise.resolve());
    const monitorSipQueueRef = useRef(Promise.resolve());
    const monitorCallBootstrappedRef = useRef({});
    const monitorSipBootstrappedRef = useRef({});

    const [autoRefetchEnabled, setAutoRefetchEnabled] = useState(() => {
        // Load the initial value from localStorage
        const savedState = localStorage.getItem('autoRefetchEnabled');
        return savedState !== null ? JSON.parse(savedState) : true; // Default to true if no value is saved
    });
    const isRecent = (dateString) => {
        const smsTime = new Date(dateString);
        const currentTime = new Date();
        const differenceInMilliseconds = currentTime - smsTime;
        const differenceInMinutes = differenceInMilliseconds / (60 * 1000);

        return differenceInMinutes <= 60;
    };
    const [open, setOpen] = useState(false);
    const [currentList, setCurrentList] = useState([]);
    const [smsLogs, setSmsLogs] = useState([]);
    const [smsCounts, setSmsCounts] = useState({ g1: 0, g2: 0, g3: 0, g4: 0, g5: 0, g6: 0, g7: 0, g8: 0, g9: 0 });
    const [smsEarnings, setSmsEarnings] = useState({ g1: 0.0, g2: 0.0, g3: 0.0, g4: 0.0, g5: 0.0, g6: 0.0, g7: 0.0, g8: 0.0, g9: 0.0 });
    const [timeFilter, setTimeFilter] = useState('all'); // Default to 'all'
    const statusCounts = {
        voipIdle: 0,
        smsIdle: 0,
        calling: 0,
        lock: 0,
        block: 0,
        simError: 0,
        noSim: 0,
        simInserted: 0,
        registering: 0,
        regFailed: 0,
        sending: 0,
        mobileData: 0
    };
    const [loadingByNumber, setLoadingByNumber] = useState({});
    const [registrationData, setRegistrationData] = useState({});
    const [openBotDialog, setOpenBotDialog] = useState(false);
    const [botMessage, setBotMessage] = useState('');
    const [openCustomMessageDialog, setOpenCustomMessageDialog] = useState(false);
    const [customMessage, setCustomMessage] = useState('');
    const [routes, setRoutes] = useState({});

    const [expiryData, setExpiryData] = useState(() => {
        const storedExpiryData = localStorage.getItem('expiryData');
        return storedExpiryData ? JSON.parse(storedExpiryData) : { goip1: {}, goip2: {}, goip3: {}, goip4: {}, goip5: {}, goip6: {}, goip7: {}, goip8: {}, goip9: {} };
    });
    const lastExpiryFetchAtRef = useRef({});

    const [cacheEnabled, setCacheEnabled] = useState(true);
    const [loading, setLoading] = useState(false);

    const parseTimeToSeconds = (value) => {
        if (value === null || value === undefined) return null;
        const raw = String(value).trim();
        if (!raw || raw === '-') return null;
        const parts = raw.split(':').map(part => Number(part));
        if (parts.some(Number.isNaN) || parts.length > 3) return null;
        if (parts.length === 3) {
            return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
        }
        if (parts.length === 2) {
            return (parts[0] * 60) + parts[1];
        }
        return parts[0];
    };

    const formatSeconds = (totalSeconds) => {
        const safeSeconds = Math.max(0, Number(totalSeconds) || 0);
        const hours = Math.floor(safeSeconds / 3600);
        const minutes = Math.floor((safeSeconds % 3600) / 60);
        const seconds = Math.floor(safeSeconds % 60);
        const pad = (num) => num.toString().padStart(2, '0');
        if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
        if (minutes > 0) return `${minutes}:${pad(seconds)}`;
        return `${seconds}`;
    };

    const formatTimerDisplay = (value) => {
        if (value === null || value === undefined) return '-';
        const parsedSeconds = parseTimeToSeconds(value);
        if (parsedSeconds === null) return String(value);
        return formatSeconds(parsedSeconds);
    };

    const getSmoothTime = (goipNumber, portNumber, fallback) => {
        const entry = callTimeBase[`goip${goipNumber}`]?.[portNumber];
        if (!entry || entry.baseSeconds === null || entry.baseSeconds === undefined) {
            return formatTimerDisplay(fallback);
        }
        const elapsedSeconds = Math.max(0, Math.floor((Date.now() - entry.syncAt) / 1000));
        return formatTimerDisplay(entry.baseSeconds + elapsedSeconds);
    };

    const getFetchApiUrl = (goipNumber) =>
        goipNumber === 9
            ? `https://frek.cc/proxy/38.54.86.24:5080/fetch-api/goip${goipNumber}`
            : `https://frek.cc/proxy/38.54.86.24:5090/fetch-api/goip${goipNumber}`;

    const getFetchSmsStatusUrl = (goipNumber) =>
        goipNumber === 9
            ? `https://frek.cc/proxy/38.54.86.24:5080/fetch-api/goip${goipNumber}-sms-status`
            : `https://frek.cc/proxy/38.54.86.24:5090/fetch-api/goip${goipNumber}-sms-status`;

    const getMonitorCallUrl = (goipNumber) =>
        goipNumber === 9
            ? `https://frek.cc/proxy/38.54.86.24:6687/monitor-call/g${goipNumber}`
            : `https://frek.cc/proxy/38.54.86.24:7687/monitor-call/g${goipNumber}`;

    const getMonitorSipUrl = (goipNumber) =>
        goipNumber === 9
            ? `https://frek.cc/proxy/38.54.86.24:6687/monitor-sip/g${goipNumber}`
            : `https://frek.cc/proxy/38.54.86.24:7687/monitor-sip/g${goipNumber}`;

    const extractContentText = (rawText) => {
        const trimmed = String(rawText || '').trim();
        if (!trimmed) return '';
        try {
            const parsed = JSON.parse(trimmed);
            if (typeof parsed?.content === 'string') {
                return parsed.content;
            }
        } catch (error) {
            // Not JSON, keep original text.
        }
        return trimmed;
    };



    // Update localStorage whenever the state changes
    useEffect(() => {
        localStorage.setItem('autoRefetchEnabled', JSON.stringify(autoRefetchEnabled));
    }, [autoRefetchEnabled]);

    // Function to handle the switch toggle
    const handleToggleSwitch = () => {
        setAutoRefetchEnabled((prev) => !prev);
    };




    const fetchCacheStatus = async () => {
        try {
            const res = await fetch('https://frek.cc/proxy/38.54.86.24:5090/toggle-cache'); // Update with your actual backend URL
            const data = await res.json();
            setCacheEnabled(data.enabled);
        } catch (error) {
            console.error('Failed to fetch cache status:', error);
        }
    };


    const updateCacheStatus = async (enabled) => {
        setLoading(true);
        try {
            await fetch('https://frek.cc/proxy/38.54.86.24:5090/toggle-cache', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled }),
            });
            setCacheEnabled(enabled);
            handleNotification(`Goip data cache updating toggled success !`);
        } catch (error) {
            console.error('Failed to update cache status:', error);
        }
        setLoading(false);
    };

    const handleToggle = (event) => {
        updateCacheStatus(event.target.checked);
    };

    const fetchRoute = async (goipNumber) => {
        const now = Date.now();
        const last = lastMonitorSipFetchAtRef.current[goipNumber] || 0;
        if ((now - last) < MONITOR_7687_SIP_MIN_INTERVAL_MS) {
            return;
        }
        lastMonitorSipFetchAtRef.current[goipNumber] = now;

        try {
            const response = await axios.get(getMonitorSipUrl(goipNumber));
            const accounts = response.data.map(item => item.Account);

            let route = '';

            // Define CCA patterns
            const patterns = {
                CCA1: ['Edz-route-63cc', 'Edz-route-a63cc', 'Frek-route-a63cc', 'Frek-route-63cc'],
                CCA2: ['Edz-route-a63cc', 'Edz-route-63cc', 'Frek-route-63cc', 'Frek-route-a63cc'],
                CCA3: ['Frek-route-63cc', 'Frek-route-a63cc', 'Edz-route-63cc', 'Edz-route-a63cc'],
                CCA4: ['Frek-route-a63cc', 'Frek-route-63cc', 'Edz-route-a63cc', 'Edz-route-63cc'],
            };

            const checkCCA = () => {
                for (const [key, pattern] of Object.entries(patterns)) {
                    let isMatch = true;
                    for (let i = 0; i < pattern.length; i++) {
                        const slice = accounts.slice(i * 8, (i + 1) * 8);
                        if (!slice.every(acc => acc.includes(pattern[i]))) {
                            isMatch = false;
                            break;
                        }
                    }
                    if (isMatch) return key;
                }
                return '';
            };

            const checkNCLIOnly = () => {
                const hasEdzNCLI = accounts.some(acc => /Edz-route-63-[1-5]/.test(acc));
                const hasFrekNCLI = accounts.some(acc => /Frek[1-5]-route-63/.test(acc));
                const onlyNCLI = accounts.every(acc =>
                    /Edz-route-63-[1-5]/.test(acc) || /Frek[1-5]-route-63/.test(acc)
                );

                if (hasEdzNCLI && hasFrekNCLI && onlyNCLI) {
                    return 'NCLI';
                }
                return '';
            };


const checkCC = () => {
    const hasEdz63 = accounts.some(acc => acc.includes('Edz-route-63cc'));
    const hasEdzA63 = accounts.some(acc => acc.includes('Edz-route-a63cc'));
    const hasFrek63 = accounts.some(acc => acc.includes('Frek-route-63cc'));
    const hasFrekA63 = accounts.some(acc => acc.includes('Frek-route-a63cc'));
    const hasEdzB63 = accounts.some(acc => acc.includes('Edz-route-b63cc'));
    const hasFrekB63 = accounts.some(acc => acc.includes('Frek-route-b63cc'));
    const hasFrekC63 = accounts.some(acc => acc.includes('Frek-route-c63cc'));
    const hasEdzC63 = accounts.some(acc => acc.includes('Edz-route-c63cc'));
    const hasFrekD63 = accounts.some(acc => acc.includes('Frek-route-d63cc'));
    const hasEdzD63 = accounts.some(acc => acc.includes('Edz-route-d63cc'));

    // ✅ New ones
    const hasEdzE63 = accounts.some(acc => acc.includes('Edz-route-e63cc'));
    const hasFrekE63 = accounts.some(acc => acc.includes('Frek-route-e63cc'));
    const hasEdzF63 = accounts.some(acc => acc.includes('Edz-route-f63cc'));
    const hasFrekF63 = accounts.some(acc => acc.includes('Frek-route-f63cc'));

    const hasWrldfax1 = accounts.some(acc => acc.includes('0112R0033NCLI63'));
    const hasWrldfax2 = accounts.some(acc => acc.includes('0112R0033NCLI63-2'));
    const hasNCLI = accounts.some(acc => /Edz-route-63-[1-5]/.test(acc));

    if (hasEdz63 && hasEdzA63 && hasFrek63 && hasFrekA63 && hasEdzB63 && hasFrekB63 && hasNCLI) {
        return 'NCC';
    }
    if (hasEdz63 && hasEdzA63 && hasFrek63 && hasFrekA63 && hasEdzB63 && hasFrekB63) {
        return 'CC';
    } else if (hasEdz63 && hasEdzA63 && hasEdzB63) {
        return 'ECC';
    } else if (hasFrek63 && hasFrekA63 && hasFrekB63) {
        return 'FCC';
    } else if (hasEdz63 && hasFrek63) {
        return 'CCm';
    } else if (hasEdzA63 && hasFrekA63) {
        return 'CCa';
    } else if (hasEdzB63 && hasFrekB63) {
        return 'CCb';
    } else if (hasEdz63) {
        return 'CC1';
    } else if (hasEdzA63) {
        return 'CC2';
    } else if (hasFrek63) {
        return 'CC3';
    } else if (hasFrekA63) {
        return 'CC4';
    } else if (hasEdzB63) {
        return 'CC5';
    } else if (hasWrldfax2) {
        return 'wfx2';
    } else if (hasWrldfax1) {
        return 'wfx1';
    } else if (hasFrekB63) {
        return 'CC6';
    } else if (hasFrekC63) {
        return 'CC7';
    } else if (hasEdzC63) {
        return 'CC8';
    } else if (hasEdzD63) {
        return 'CC9';
    } else if (hasFrekD63) {
        return 'CC10';
    } 
    // ✅ New CC mappings
    else if (hasEdzE63) {
        return 'CC11';
    } else if (hasFrekE63) {
        return 'CC12';
    } else if (hasEdzF63) {
        return 'CC13';
    } else if (hasFrekF63) {
        return 'CC14';
    }

    return '';
};


            const checkOtherRoutes = () => {
                if (accounts.some(acc => /1443R517-63CLI|1443R518-63CLI/.test(acc))) {
                    return 'SK';
                } else if (accounts.some(acc => /frek_qb/.test(acc))) {
                    return 'QBerry';
                } else if (accounts.some(acc => /0014R648-63|0014R649-63/.test(acc))) {
                    return 'Maize';
                } else if (accounts.some(acc => /0014R651-63/.test(acc))) {
                    return 'MZ';
                } else if (accounts.some(acc => /1443R517-63CC/.test(acc))) {
                    return 'SK';
                } else if (accounts.some(acc => /1443R518-63CC/.test(acc))) {
                    return 'SK1';
                } else if (accounts.some(acc => /1443R519-63CC/.test(acc))) {
                    return 'SK2';
                } else if (accounts.some(acc => /Edz-route-63-[1-5]/.test(acc))) {
                    return 'NCLIE';
                } else if (accounts.some(acc => /Frek[1-5]-route-63/.test(acc))) {
                    return 'NCLIF';
                }
                return '';
            };

            // Determine the route: prioritize CCA > NCLI Only > CC > others
            route = checkCCA() || checkNCLIOnly() || checkCC() || checkOtherRoutes() || '';

            // Update routes
            setRoutes(prevRoutes => ({ ...prevRoutes, [goipNumber]: route }));
        } catch (error) {
            console.error('Failed to fetch data:', error);
            setRoutes(prevRoutes => ({ ...prevRoutes, [goipNumber]: 'Err' }));
        }
    };

    const enqueueMonitorSip = (goipNumber, options = {}) => {
        const { immediate = false } = options;
        if (immediate || !monitorSipBootstrappedRef.current[goipNumber]) {
            monitorSipBootstrappedRef.current[goipNumber] = true;
            return fetchRoute(goipNumber);
        }
        monitorSipQueueRef.current = monitorSipQueueRef.current
            .then(async () => {
                if (!isPageVisibleRef.current) return;
                await fetchRoute(goipNumber);
            })
            .catch((error) => console.error('monitor-sip queue error:', error));
        return monitorSipQueueRef.current;
    };

    // UPDATED: This useEffect now fetches routes for the *active* GoIPs
    useEffect(() => {
        if (activeGoips.length === 0) return;

        const run = () => {
            if (!isPageVisibleRef.current) return;
            activeGoips.forEach(number => {
                enqueueMonitorSip(number);
            });
        };

        Promise.all(activeGoips.map(number => enqueueMonitorSip(number, { immediate: true })))
            .catch((error) => console.error('monitor-sip bootstrap error:', error));
        const interval = setInterval(run, ROUTE_POLL_MS);

        return () => clearInterval(interval);
    }, [activeGoips]); // Re-run if the list of active GoIPs changes


    async function fetchSmsData() {
        const response = await fetch("https://frek.cc/proxy/38.54.86.24:5005/get-sms");
        const smsData = await response.json();

        // Filter the SMS data for recent messages
        const recentSms = smsData.filter(sms => isRecent(sms.date));

        setRecentSmsData(recentSms);
    }

    function determineRowColor(item1) {
        const relatedSms = recentSmsData.filter(sms => sms.sender === item1);

        if (relatedSms.length === 0) return "";

        const has02 = relatedSms.some(sms => sms.content.startsWith("globe"));
        const has03 = relatedSms.some(sms => sms.content.startsWith("smart"));

        if (has02 && has03) return "#7B68EE";
        if (has02) return "#3CB371";
        if (has03) return "#BDB76B";

        return "";  // default color
    }

    useEffect(() => {
        fetchSmsData(); // Fetch immediately upon mounting

        const intervalId = setInterval(() => {
            if (!isPageVisibleRef.current) return;
            fetchSmsData(); // Fetch every 1 hour
        }, RECENT_SMS_POLL_MS);

        return () => clearInterval(intervalId); // Clear the interval when the component unmounts
    }, []);

    const [callStatsData, setCallStatsData] = useState({ goip1: [], goip2: [], goip3: [], goip4: [], goip5: [], goip6: [], goip7: [], goip8: [], goip9: [] });







    const handleSendRandomSms = async (goipNumber) => {
        try {
            // Ensure smsStatusData exists for the specified GOIP
            if (!smsStatusData[`goip${goipNumber}`]) {
                console.error(`No SMS status data found for goip${goipNumber}`);
                return;
            }

            // Filter ports where index 7 has a value >= 1
            const ports = smsStatusData[`goip${goipNumber}`]
                .filter(item => item[7] >= 1) // Ensure index 7 value is >= 1
                .map(item => item[0]); // Extract the port (item[0])

            if (ports.length === 0) {
                handleNotification(`No ports with >= 1 FS for goip${goipNumber}`);
                return; // Exit if no valid ports found
            }

            // Generate random data for SMS
            const randomPrefix = ['63910', '63951', '63946'];
            const randomSuffix = Math.floor(1000000 + Math.random() * 8999999); // Ensure a 7-digit random suffix
            const goip_sms_dst = `${randomPrefix[Math.floor(Math.random() * randomPrefix.length)]}${randomSuffix}`;
            const goip_sms_send = Math.random().toString(36).substring(2, 5); // Random 3-character string

            // Create payload
            const payload = {
                goip_sms_dst,
                goip_sms_send,
                goip_cmd_port: ports.join(',') // Combine ports with a comma
            };

            console.log('Payload:', payload);

            // Send POST request
            const response = await axios.post(`https://frek.cc/proxy/38.54.86.24:7687/send_sms/g${goipNumber}`, payload, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.status === 200) {
                handleNotification(`SMS sent successfully for goip${goipNumber}`);
            } else {
                handleNotification(`Failed to send SMS for goip${goipNumber}`);
            }
        } catch (error) {
            console.error(`Error sending SMS for goip${goipNumber}`, error);
        }
    };







    const handleNotification = (message, severity = 'success') => {
        showNotification(message, severity);
    };


    // Helper function to determine if the text should flash
    function shouldFlash(timestamp) {
        const remainingTime = getTimeDifferenceInSeconds(timestamp);
        return remainingTime <= 0 || remainingTime < 7200; // Flash if expired or less than 1 hour left
    }

    // Helper function to determine color based on the time left
    function getColorBasedOnTimeLeft(timestamp) {
        const remainingTime = getTimeDifferenceInSeconds(timestamp);

        if (remainingTime <= 0) return '#986868'; // Expired
        if (remainingTime < 7200) return '#6F8FAF'; // Less than 1 hour
        if (remainingTime < 86400) return '#C2B280'; // Less than 24 hours
        return ''; // More than 24 hours
    }

    // Helper function to get the time difference in seconds
    function getTimeDifferenceInSeconds(timestamp) {
        const now = new Date();
        const expiryDate = new Date(timestamp);
        return Math.floor((expiryDate - now) / 1000);
    }

    const BAL_DAY_SECONDS = 24 * 60 * 60;
    function mixHexColors(startHex, endHex, ratio) {
        const clampRatio = Math.max(0, Math.min(1, ratio));
        const start = startHex.replace('#', '');
        const end = endHex.replace('#', '');
        const mixed = [0, 2, 4]
            .map(offset => {
                const startValue = parseInt(start.slice(offset, offset + 2), 16);
                const endValue = parseInt(end.slice(offset, offset + 2), 16);
                const value = Math.round(startValue + (endValue - startValue) * clampRatio);
                return value.toString(16).padStart(2, '0');
            })
            .join('');

        return `#${mixed}`;
    }

    function getBalProgressMeta(timestamp) {
        if (!timestamp || isNaN(new Date(timestamp).getTime())) {
            return { percent: 0, label: '--', color: 'rgba(255,255,255,0.2)', animate: false, expired: false };
        }

        const remainingSeconds = getTimeDifferenceInSeconds(timestamp);

        if (remainingSeconds <= 0) {
            return { percent: 100, label: 'Exp', color: '#ff6b6b', animate: true, expired: true };
        }

        if (remainingSeconds > BAL_DAY_SECONDS) {
            const secondDaySeconds = remainingSeconds - BAL_DAY_SECONDS;
            const dayTwoPercent = Math.max(0, Math.min(100, (secondDaySeconds / BAL_DAY_SECONDS) * 100));

            return {
                percent: dayTwoPercent,
                label: dayTwoPercent >= 99 ? 'Fresh' : 'Day 2',
                color: mixHexColors('#9acd32', '#5ad27a', dayTwoPercent / 100),
                animate: false,
                expired: false,
            };
        }

        return {
            percent: Math.max(0, Math.min(100, (remainingSeconds / BAL_DAY_SECONDS) * 100)),
            label: remainingSeconds <= 12 * 60 * 60 ? 'Low' : 'Day 1',
            color: remainingSeconds <= 12 * 60 * 60 ? '#ff6b6b' : '#f2994a',
            animate: remainingSeconds <= 6 * 60 * 60,
            expired: false,
        };
    }




    const fetchApiData = async (goipNumber) => {
        try {
            const response = await fetch(getFetchApiUrl(goipNumber));
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const textData = extractContentText(await response.text());

            // Adjusted regular expression to ignore lines starting with //
            const regex = /^(?!\/\/).*var strLocNumList = '({.*?})';/ms;
            const matches = textData.match(regex);
            if (matches && matches[1]) {
                let jsonString = matches[1];

                // Replace escaped quotes with regular quotes
                jsonString = jsonString.replace(/\\"/g, '"');

                // Ensure the jsonString is valid JSON
                if (jsonString[0] !== '{' || jsonString[jsonString.length - 1] !== '}') {
                    throw new Error('Extracted string is not valid JSON');
                }

                const jsonData = JSON.parse(jsonString);

                // Log the parsed JSON
                console.log('Parsed JSON:', jsonData);

                // Assuming you want to save this data in state
                setData(prevData => ({ ...prevData, [`goip${goipNumber}`]: jsonData.data }));
            } else {
                throw new Error('Data not found in the fetched string.');
            }
        } catch (error) {
            console.error('Error fetching or parsing data:', error);
            setError(error);
        }
    };

    // Adjust the auto-refetch logic to respect the switch state
    useEffect(() => {
        if (!autoRefetchEnabled) return; // Skip auto-refetch if disabled

        const expiredItems = Object.keys(expiryData).flatMap(goipKey =>
            Object.entries(expiryData[goipKey] || {}).filter(([index, timestamp]) => {
                const timeLeft = getTimeDifferenceInSeconds(timestamp);
                return timeLeft <= 0;
            }).map(([index]) => ({ goipKey, index }))
        );

        if (expiredItems.length > 0) {
            expiredItems.forEach(({ goipKey, index }) => {
                const rowIndex = Number(index);
                const goipRows = data[goipKey];
                const currentRow = Array.isArray(goipRows) ? goipRows[rowIndex] : null;
                const currentItem = currentRow ? currentRow[1] : null;

                if (!currentItem) {
                    return;
                }

                fetchExpiryForSim(currentItem, goipKey, rowIndex);
            });
        }
    }, [expiryData, data, autoRefetchEnabled]);


    const fetchSmsStatusData = async (goipNumber) => {
        try {
            // If you still need different base URLs, you can parameterize them or pick one by rules:
            // e.g., switch on goipNumber or pass a separate arg. For demonstration, let's just use the same domain:
            const response = await fetch(getFetchSmsStatusUrl(goipNumber));
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const textData = extractContentText(await response.text());

            // Try both regex patterns:
            const regexList = [
                /var strSmsStatList = '({.*?})';/ms,     // Pattern 1
                /splitPortDataFormat\('({.*?})'/ms       // Pattern 2
            ];

            let jsonString = null;
            for (const regex of regexList) {
                const match = textData.match(regex);
                if (match && match[1]) {
                    jsonString = match[1];
                    break; // stop at first successful match
                }
            }

            if (!jsonString) {
                throw new Error("SMS status data not found in the fetched string (both patterns failed).");
            }

            // Replace escaped quotes
            jsonString = jsonString.replace(/\\"/g, '"');

            // Validate JSON structure
            if (jsonString[0] !== '{' || jsonString[jsonString.length - 1] !== '}') {
                throw new Error('Extracted string is not valid JSON');
            }

            // Parse
            const jsonData = JSON.parse(jsonString);

            // Filter out "Total" if needed
            const filteredData = jsonData.data.filter(entry => entry[1] !== "Total");

            // Map status codes to messages
            const statusCodes = {
                3: 'Idle',
                4: 'Calling',
                7: 'Lock',
                10: 'Blocked',
                9: 'SimErr',
                0: 'NoS',
                11: 'iSim',
                2: 'Reg',
                6: 'RegF',
            };

            // Map data
            const mappedData = filteredData.map(entry => ({
                ...entry,
                status: statusCodes[entry[2]] || 'Unknown'
            }));

            // Update state (assuming you have these setSmsStatusData and setError in scope)
            setSmsStatusData(prevData => ({ ...prevData, [`goip${goipNumber}`]: mappedData }));

        } catch (error) {
            console.error('Error fetching or parsing SMS status data:', error);
            setError(error);
        }
    };

    const fetchCallStatsData = async (goipNumber) => {
        const now = Date.now();
        const last = lastMonitorCallFetchAtRef.current[goipNumber] || 0;
        if ((now - last) < MONITOR_CALL_MIN_INTERVAL_MS) {
            return;
        }
        lastMonitorCallFetchAtRef.current[goipNumber] = now;

        try {
            const response = await fetch(getMonitorCallUrl(goipNumber));
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const jsonData = await response.json();
            const normalized = Array.isArray(jsonData)
                ? jsonData
                    .map((item) => {
                        const portRaw = String(item?.Port ?? '').trim();
                        if (!portRaw || portRaw.toUpperCase() === 'TOTAL') return null;
                        return {
                            ...item,
                            Port: portRaw,
                            PortBase: portRaw.split('.')[0],
                        };
                    })
                    .filter(Boolean)
                : [];
            setCallStatsData(prevData => ({ ...prevData, [`goip${goipNumber}`]: normalized }));
            const syncAt = Date.now();
            const baseTimes = {};
            normalized.forEach(item => {
                const seconds = parseTimeToSeconds(item.Time);
                if (seconds !== null) {
                    baseTimes[item.Port] = { baseSeconds: seconds, syncAt };
                } else {
                    baseTimes[item.Port] = null;
                }
            });
            if (Object.keys(baseTimes).length > 0) {
                setCallTimeBase(prevData => ({
                    ...prevData,
                    [`goip${goipNumber}`]: {
                        ...prevData[`goip${goipNumber}`],
                        ...baseTimes,
                    },
                }));
            }
        } catch (error) {
            if (error?.name === 'AbortError' || String(error).toLowerCase().includes('aborted')) {
                return;
            }
            console.error('Error fetching call stats data:', error);
            setError(error);
        }
    };



    function formatTimeDifference(timestamp) {
        if (!timestamp || isNaN(new Date(timestamp).getTime())) {
            return "-"; // Return "-" for invalid or missing timestamps
        }

        const inputDate = new Date(timestamp);
        const bannedDateParts = '1970-01-01 13:00:00'.split(/[- :]/);
        const bannedDate = new Date(
            bannedDateParts[0],
            bannedDateParts[1] - 1,
            bannedDateParts[2],
            bannedDateParts[3],
            bannedDateParts[4],
            bannedDateParts[5]
        );

        if (inputDate.getTime() === bannedDate.getTime()) {
            return "Ban"; // Return "Ban" for banned dates
        }

        const now = new Date();
        let diffInSeconds = Math.floor((inputDate - now) / 1000);

        if (diffInSeconds <= 0) {
            return "Exp"; // Return "Exp" for expired timestamps
        }

        // Calculate days, hours, minutes, and seconds
        const days = Math.floor(diffInSeconds / (3600 * 24));
        const remainingSecondsAfterDays = diffInSeconds % (3600 * 24);

        const hours = Math.floor(remainingSecondsAfterDays / 3600);
        const remainingSecondsAfterHours = remainingSecondsAfterDays % 3600;

        const minutes = Math.floor(remainingSecondsAfterHours / 60);
        const seconds = remainingSecondsAfterHours % 60;

        // Format the output based on your requirements
        if (days > 0) {
            if (hours > 0) {
                return `${days}d ${hours}h`; // Days and hours
            } else if (minutes > 0) {
                return `${days}d ${minutes}m`; // Days and minutes
            } else {
                return `${days}d ${seconds}s`; // Days and seconds
            }
        } else if (hours > 0) {
            if (minutes > 0) {
                return `${hours}h ${minutes}m`; // Hours and minutes
            } else {
                return `${hours}h ${seconds}s`; // Hours and seconds
            }
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s`; // Minutes and seconds
        } else {
            return `${seconds}s`; // Seconds only
        }
    }















    async function fetchExpiryForSim(sim, goipKey, index, options = {}) {
        const { force = false } = options;
        const simKey = String(sim || '').trim();
        if (!simKey) return null;

        const now = Date.now();
        const lastAttempt = lastExpiryFetchAtRef.current[simKey] || 0;
        const isCooldown = (now - lastAttempt) < EXPIRY_FETCH_COOLDOWN_MS;
        if (!force && isCooldown) {
            return expiryData?.[goipKey]?.[index] || null;
        }
        lastExpiryFetchAtRef.current[simKey] = now;

        const endpoint = `https://frek.cc/proxy/38.54.86.24:5000/expiry/${sim}`;
        try {
            setLoadingByNumber(prev => ({ ...prev, [`${goipKey}-${index}`]: true }));

            const response = await fetch(endpoint, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "x-access-token": "uBEiuHBXrDfiuthssKUQFN4xyJzmWPj3",
                },
            });

            if (response.ok) {
                const data = await response.json();
                const timestamp = data[0]?.value;

                if (timestamp && !isNaN(new Date(timestamp).getTime())) {
                    // Valid expiry data, update state
                    setExpiryData(prevExpiryData => ({
                        ...prevExpiryData,
                        [goipKey]: { ...prevExpiryData[goipKey], [index]: timestamp },
                    }));
                    return timestamp; // Return the valid expiry timestamp
                } else {
                    // Clear expiry data if invalid
                    setExpiryData(prevExpiryData => {
                        const updatedExpiryData = { ...prevExpiryData };
                        if (updatedExpiryData[goipKey]) {
                            delete updatedExpiryData[goipKey][index];
                        }
                        return updatedExpiryData;
                    });
                    return null; // Explicitly return null for invalid data
                }
            } else {
                console.error("Request failed with status:", response.status);
                return null;
            }
        } catch (error) {
            console.error("An error occurred:", error);
            return null;
        } finally {
            setLoadingByNumber(prev => ({ ...prev, [`${goipKey}-${index}`]: false }));
        }
    }

    // NEW: Function to get the list of active GoIPs from your endpoint
    const fetchActiveGoips = async () => {
        try {
            const urls = [
                'https://frek.cc/proxy/38.54.86.24:5000/expiry/64746278498', // GOIP 1..8
                'https://frek.cc/proxy/38.54.86.24:5000/expiry/4587964455', // GOIP 9
            ];
            const responses = await Promise.all(urls.map(url => fetch(url)));
            responses.forEach((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            });

            const payloads = await Promise.all(responses.map(response => response.json()));
            const parsed = payloads.flatMap((payload) => {
                const rawValue = payload?.[0]?.value;
                if (!rawValue) return [];
                return String(rawValue)
                    .split(',')
                    .map(value => Number(String(value).trim()))
                    .filter(Number.isFinite);
            });

            const activeList = Array.from(new Set(parsed)).sort((a, b) => a - b);
            setActiveGoips((prev) => {
                if (
                    prev.length === activeList.length &&
                    prev.every((value, index) => value === activeList[index])
                ) {
                    return prev;
                }
                return activeList;
            });
            return activeList;
        } catch (error) {
            console.error("Failed to fetch active GoIP list:", error);
            setActiveGoips([]); // Clear active list on error
            return []; // Return empty array on failure
        }
    };

    const enqueueMonitorCall = (goipNumber, options = {}) => {
        const { immediate = false } = options;
        if (immediate || !monitorCallBootstrappedRef.current[goipNumber]) {
            monitorCallBootstrappedRef.current[goipNumber] = true;
            return fetchCallStatsData(goipNumber);
        }
        monitorCallQueueRef.current = monitorCallQueueRef.current
            .then(async () => {
                if (!isPageVisibleRef.current) return;
                await fetchCallStatsData(goipNumber);
            })
            .catch((error) => console.error('monitor-call queue error:', error));
        return monitorCallQueueRef.current;
    };

    useEffect(() => {
        activeGoipsRef.current = activeGoips;
    }, [activeGoips]);

    useEffect(() => {
        const onVisibilityChange = () => {
            isPageVisibleRef.current = !document.hidden;
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);

    // UPDATED: Main data fetching logic
    useEffect(() => {
        document.title = 'Goip Stats Local';

        const fetchDataForActive = async (showLoader = false, goipListOverride = null) => {
            if (!isPageVisibleRef.current) return;
            if (fetchCycleRunningRef.current) return;
            fetchCycleRunningRef.current = true;
            if (showLoader) setIsLoading(true);
            try {
                const activeGoipList = Array.isArray(goipListOverride)
                    ? goipListOverride
                    : activeGoipsRef.current;
                if (activeGoipList.length > 0) {
                    await Promise.all(
                        activeGoipList.map(async (number) => {
                            await Promise.all([
                                fetchSmsStatusData(number),
                                fetchApiData(number),
                            ]);
                            enqueueMonitorCall(number, { immediate: showLoader });
                        })
                    );
                }
            } catch (error) {
                console.error('An error occurred during the fetch cycle:', error);
            } finally {
                if (showLoader) setIsLoading(false);
                fetchCycleRunningRef.current = false;
            }
        };

        const init = async () => {
            const initialActiveGoips = await fetchActiveGoips();
            await fetchDataForActive(true, initialActiveGoips);
            fetchCacheStatus();
        };

        init();
        const dataIntervalId = setInterval(() => fetchDataForActive(false), DATA_POLL_MS);
        const activeIntervalId = setInterval(() => {
            if (!isPageVisibleRef.current) return;
            fetchActiveGoips();
        }, ACTIVE_GOIP_REFRESH_MS);

        return () => {
            clearInterval(dataIntervalId);
            clearInterval(activeIntervalId);
        };
    }, []); // This effect should only run once on mount




    useEffect(() => {
        // Save expiryData to localStorage whenever it changes
        localStorage.setItem('expiryData', JSON.stringify(expiryData));
    }, [expiryData]);



    useEffect(() => {
        if (!isLoading) {
            const allPromises = Object.keys(data).flatMap(goipKey => {
                return data[goipKey].map((item, index) => {
                    const currentItem = item[1]; // Current number or item
                    const prevItem = prevDataRef.current[goipKey]?.[index];
                    const expiryTimestamp = expiryData[goipKey]?.[index];
                    const timeLeft = expiryTimestamp ? getTimeDifferenceInSeconds(expiryTimestamp) : -1;

                    if (!currentItem) {
                        // Clear expiry data if the current item is empty
                        setExpiryData(prevExpiryData => ({
                            ...prevExpiryData,
                            [goipKey]: {
                                ...prevExpiryData[goipKey],
                                [index]: undefined, // Clear the expiry data for this index
                            },
                        }));
                        return null;
                    }

                    // Skip if expiry data exists and is still valid
                    if (expiryTimestamp && timeLeft > 0) {
                        return null;
                    }

                    // Skip if the current item is the same as the previous one
                    if (currentItem === prevItem) {
                        return null;
                    }

                    // Update prevDataRef for the current key and index
                    prevDataRef.current[goipKey] = prevDataRef.current[goipKey] || {};
                    prevDataRef.current[goipKey][index] = currentItem;

                    // Proceed to fetch expiry data
                    return new Promise(resolve => {
                        setLoadingByNumber(prev => ({ ...prev, [`${goipKey}-${index}`]: true }));
                        setTimeout(() => {
                            fetchExpiryForSim(currentItem, goipKey, index)
                                .then(() => {
                                    // Add additional delay after fetching is complete
                                    setTimeout(() => {
                                        setLoadingByNumber(prev => ({ ...prev, [`${goipKey}-${index}`]: false }));
                                    }, 3000); // 3 seconds delay
                                    resolve();
                                })
                                .catch(() => {
                                    setLoadingByNumber(prev => ({ ...prev, [`${goipKey}-${index}`]: false }));
                                });
                        }, 15000); // Delay before fetching expiry
                    });
                }).filter(promise => promise !== null); // Filter out null values
            });

            if (allPromises.length > 0) {
                Promise.all(allPromises).then(() => {
                    setExpiryDataLoading(false);
                });
            }
        }
    }, [data, isLoading, expiryData]);

















    useEffect(() => {
        const intervalId = setInterval(() => {
            setTick(tick => tick + 1);
        }, 1000);

        return () => clearInterval(intervalId);
    }, []);

    const renderTable = (goipNumber) => {
        // Initialize totals
        let totalCalls = 0;
        let totalAlerted = 0;
        let totalConnected = 0;
        let totalNoAlertCalls = 0;
        let totalFailedCalls = 0;
        let totalConFailedCall = 0;
        let totalConNoAlertCall = 0;




        let totalCallingPorts = 0;
        let totalNoSimPorts = 0;
        let totalLockPorts = 0;
        let totalSendingPorts = 0; // Variable for total sending ports
        const totalPorts = data[`goip${goipNumber}`].length;
        const smsRows = Array.isArray(smsStatusData[`goip${goipNumber}`])
            ? smsStatusData[`goip${goipNumber}`]
            : [];

        const getSmsRowByPort = (portValue, fallbackIndex = null) => {
            const rawPort = String(portValue ?? '').trim();
            const portBase = rawPort.split('.')[0];
            const matched = smsRows.find((row) => {
                const smsPortRaw = String(row?.[0] ?? '').trim();
                if (!smsPortRaw) return false;
                if (smsPortRaw === rawPort) return true;
                return smsPortRaw.split('.')[0] === portBase;
            });
            if (matched) return matched;
            if (fallbackIndex !== null && Array.isArray(smsRows[fallbackIndex])) {
                return smsRows[fallbackIndex];
            }
            return null;
        };

        // Iterate over each port's status
        data[`goip${goipNumber}`].forEach((item, index) => {
            const smsRow = getSmsRowByPort(item?.[0], index);
            if (smsRow) {
                const callStatus = Number(smsRow[2]); // call status
                const sendStatus = Number(smsRow[9]); // send status
                const lockStatus = Number(smsRow[2]);
                const noSimStatus = Number(smsRow[2]);

                if (callStatus === 4) { // Check if the status is 'calling'
                    totalCallingPorts += 1;
                }
                if (lockStatus === 7) { // Check if the status is 'locked'
                    totalLockPorts += 1;
                }

                if (noSimStatus === 0) { // Check if the status is 'no sim'
                    totalNoSimPorts += 1;
                }

                if (sendStatus > 0) { // Check if the status is 'sending'
                    totalSendingPorts += 1;
                }
            }
        });




        const handleRefetchExpiry = async () => {
            const goipData = data[`goip${goipNumber}`]; // Get data for this GOIP
            if (!goipData) return;

            const updatedExpiryData = { ...expiryData };

            const refetchPromises = goipData.map(async (item, index) => {
                const currentItem = item[1]; // item1 value
                if (currentItem) {
                    // Refetch expiry for each port
                    setLoadingByNumber(prev => ({ ...prev, [`goip${goipNumber}-${index}`]: true }));
                    const expiry = await fetchExpiryForSim(currentItem, `goip${goipNumber}`, index, { force: true });

                    if (expiry) {
                        // Update with valid expiry data
                        if (!updatedExpiryData[`goip${goipNumber}`]) {
                            updatedExpiryData[`goip${goipNumber}`] = {};
                        }
                        updatedExpiryData[`goip${goipNumber}`][index] = expiry;
                    } else {
                        // Remove invalid or empty expiry data
                        if (updatedExpiryData[`goip${goipNumber}`]) {
                            delete updatedExpiryData[`goip${goipNumber}`][index];
                        }
                    }
                } else {
                    // Clear expiry data for empty currentItem
                    if (updatedExpiryData[`goip${goipNumber}`]) {
                        delete updatedExpiryData[`goip${goipNumber}`][index];
                    }
                }
            });

            // Wait for all promises to complete
            await Promise.all(refetchPromises);

            // Update state and localStorage with the final cleaned expiry data
            setExpiryData(updatedExpiryData);
            localStorage.setItem('expiryData', JSON.stringify(updatedExpiryData));

            // Clear loading states after fetching
            goipData.forEach((_, index) => {
                setLoadingByNumber(prev => ({ ...prev, [`goip${goipNumber}-${index}`]: false }));
            });

            console.log('Final expiryData:', updatedExpiryData);
            console.log('LocalStorage expiryData:', JSON.parse(localStorage.getItem('expiryData')));
        };




        // Calculate the progress percentage for calling ports
        const callProgressPercentage = (totalCallingPorts / totalPorts) * 100;
        const lockProgressPercentage = (totalLockPorts / totalPorts) * 100;
        const noSimProgressPercentage = (totalNoSimPorts / totalPorts) * 100;
        // Calculate the progress percentage for sending ports
        const sendProgressPercentage = (totalSendingPorts / totalPorts) * 100;



        // Calculate ratios
        const connectedRatio = totalCalls > 0 ? (totalConnected / totalCalls * 100).toFixed(0) : '0';
        const alertedRatio = totalCalls > 0 ? (totalAlerted / totalCalls * 100).toFixed(0) : '0';


        return (


            <ThemeProvider theme={darkTheme}>
                {/* CssBaseline is optional but generally recommended to apply base dark styles */}
                {/* <CssBaseline /> */}

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5px', justifyContent: 'center' }}>
                    <TableContainer
                        component={Paper}
                        style={{ maxWidth: '100%', width: 'fit-content', display: 'inline-block' }}
                        // sx is MUI's styled-system; you can also override here
                        sx={{
                            backgroundColor: 'rgba(20, 20, 26, 0.75)',
                            border: '1px solid rgba(255,255,255,0.04)',
                            borderRadius: 2,
                            overflowX: 'auto',
                            boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
                            minWidth: 0,
                            maxWidth: 'fit-content',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 0.5,
                                py: 0.25,
                                borderBottom: '1px solid rgba(255,255,255,0.04)',
                                background: 'rgba(255,255,255,0.02)',
                            }}
                        >
                            
                                <Typography sx={{ fontSize: 14, fontWeight: 700, color: 'text.primary' }}>
                                    G{goipNumber}
                                </Typography>
                                {routes[goipNumber] ? (
                                    <Chip
                                        label={routes[goipNumber]}
                                        size="small"
                                        sx={{
                                            bgcolor: 'rgba(255,255,255,0.06)',
                                            color: 'rgba(255,255,255,0.72)',
                                            border: '1px solid rgba(255,255,255,0.05)',
                                        }}
                                    />
                                ) : null}
                          
                            <Stack direction="row" spacing={0.25} alignItems="center">
                                <Tooltip title="Click to refresh expiry for this GOIP" arrow>
                                    <IconButton
                                        onClick={handleRefetchExpiry}
                                        size="small"
                                        sx={{
                                            color: 'rgba(255,255,255,0.6)',
                                            p: 0.25,
                                            width: 20,
                                            height: 20,
                                        }}
                                    >
                                        <RefreshIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Test Failing Ports">
                                    <IconButton
                                        onClick={() => handleSendRandomSms(goipNumber)}
                                        size="small"
                                        sx={{
                                            color: 'rgba(255,255,255,0.6)',
                                            p: 0.25,
                                            width: 20,
                                            height: 20,
                                        }}
                                    >
                                        <SendIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </Stack>
                        </Box>

                        <Box sx={{ px: 0.5, py: 0.3, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <Stack spacing={0.5}>
                                <Box>

                                    <LinearProgress
                                        variant="determinate"
                                        color="success"
                                        sx={{
                                            height: 4,
                                            borderRadius: 6,
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                        }}
                                        value={callProgressPercentage}
                                    />
                                </Box>
                                <Box>

                                    <LinearProgress
                                        variant="determinate"
                                        color="primary"
                                        sx={{
                                            height: 4,
                                            borderRadius: 6,
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                        }}
                                        value={sendProgressPercentage}
                                    />
                                </Box>
                            </Stack>
                        </Box>

                        <Table
                            size="small"
                            stickyHeader
                            aria-label={`GOIP ${goipNumber} data`}
                            sx={{
                                tableLayout: 'auto',
                                width: 'fit-content',
                                '& .MuiTableCell-root': {
                                    padding: '2px 3px',
                                },
                            }}
                        >
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            color: 'rgba(255,255,255,0.75)',
                                            backgroundColor: 'rgba(16, 16, 22, 0.7)',
                                            width: 16,
                                        }}
                                        align="center"
                                        style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
                                    >
                                        <Tooltip title="Port / Hover to reveal the number">
                                            <span>*</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            color: 'rgba(255,255,255,0.75)',
                                            backgroundColor: 'rgba(16, 16, 22, 0.7)',
                                            width: 22,
                                        }}
                                        align="center"
                                        style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
                                    >
                                        <Tooltip title="Status">
                                            <span>stt</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            color: 'rgba(255,255,255,0.75)',
                                            backgroundColor: 'rgba(16, 16, 22, 0.7)',
                                            width: 23,
                                        }}
                                        align="center"
                                        style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
                                    >
                                        <Tooltip title="Validity">
                                            <span>bal</span>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            color: 'rgba(255,255,255,0.75)',
                                            backgroundColor: 'rgba(16, 16, 22, 0.7)',
                                            width: 24,
                                        }}
                                        align="center"
                                        style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
                                    >
                                        <Tooltip title="Hourly SMS Sent | Consecutive Failed SMS Sent">
                                            <span>snt</span>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {data[`goip${goipNumber}`].map((item, index) => {
                                    const portNumber = item[0];
                                    const rowPortRaw = String(portNumber ?? '').trim();
                                    const rowPortBase = rowPortRaw.split('.')[0];
                                    const smsRow = getSmsRowByPort(rowPortRaw, index);
                                    const callStatus = Number(smsRow?.[2]);
                                    const smsStatus = Number(smsRow?.[9] ?? 0);
                                    const sentOkCount = Number(smsRow?.[7] ?? 0);
                                    const sentFailCount = Number(smsRow?.[5] ?? 0);
                                    const callStat = callStatsData[`goip${goipNumber}`]?.find(
                                        (stat) => {
                                            const statPortRaw = String(stat?.Port ?? '').trim();
                                            if (!statPortRaw || statPortRaw.toUpperCase() === 'TOTAL') return false;
                                            if (statPortRaw === rowPortRaw) return true;
                                            const statPortBase = String(stat?.PortBase ?? statPortRaw.split('.')[0]).trim();
                                            return statPortBase === rowPortBase;
                                        }
                                    );
                                    const expiryValue =
                                        expiryData[`goip${goipNumber}`] && expiryData[`goip${goipNumber}`][index]
                                            ? expiryData[`goip${goipNumber}`][index]
                                            : null;
                                    const isExpired =
                                        !!expiryValue &&
                                        !isNaN(new Date(expiryValue).getTime()) &&
                                        getTimeDifferenceInSeconds(expiryValue) <= 0;
                                    const isCritical =
                                        callStatus === 7 || // lock
                                        callStatus === 10 || // block
                                        callStatus === 9 || // sim error
                                        callStatus === 6 || // reg failed
                                        isExpired;

                                    const timerPort = callStat?.Port ?? rowPortRaw;
                                    const timerDisplay = getSmoothTime(goipNumber, timerPort, callStat?.Time || '-');

                                    const statusLabel = (() => {
                                        if (callStatus === 4 && smsStatus > 0) return 'Call + SMS';
                                        if (smsStatus > 0) return 'Sending';
                                        if (callStatus === 4) return 'Calling';
                                        return {
                                            3: 'Idle',
                                            7: 'Locked',
                                            10: 'Blocked',
                                            9: 'Sim Error',
                                            0: 'No Sim',
                                            11: 'Sim In',
                                            2: 'Registering',
                                            6: 'Reg Failed',
                                            15: 'Mobile Data',
                                        }[callStatus] || 'Unknown';
                                    })();
                                    const callStateMap = {
                                        CONNECTED: { label: 'Connected', color: '#5ad27a' },
                                        HANGUP: { label: 'Hangup', color: '#ff6b6b' },
                                        ALERTING: { label: 'Alerting', color: '#f7c948' },
                                        DISCONNECTED: { label: 'Disconnected', color: '#9aa0a6' },
                                        DIALING: { label: 'Dialing', color: '#6aa9ff' },
                                    };
                                    const callState = callStateMap[callStat?.['Call-Status']] || null;

                                    return (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                cursor: 'pointer',
                                                borderLeft: isCritical ? '3px solid rgba(255, 120, 120, 0.7)' : '3px solid transparent',
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: 'rgba(255,255,255,0.015)',
                                                },
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255,255,255,0.045)',
                                                },
                                            }}
                                        >
                                            {/* Port */}
                                            <TableCell
                                                sx={{
                                                    fontSize: 13,
                                                    color: 'text.primary',
                                                    backgroundColor: determineRowColor(item[1]),
                                                    width: 16,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                                align="center"
                                                style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
                                            >
                                                <Tooltip title={item[1]}>
                                                    <span>{item[0]}</span>
                                                </Tooltip>
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell
                                                sx={{
                                                    fontSize: 12,
                                                    color: 'text.primary',
                                                    width: 22,
                                                    overflow: 'hidden',
                                                }}
                                                align="center"
                                                style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.5,
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Tooltip title={callState ? `${statusLabel} | ${callState.label}` : statusLabel} arrow>
                                                        <Box
                                                            sx={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                borderRadius: '50%',
                                                                border: '1px solid rgba(255,255,255,0.05)',
                                                                backgroundColor: 'rgba(255,255,255,0.03)',
                                                                position: 'relative',
                                                            }}
                                                        >
                                                            {(() => {
                                                                // Original logic for icon:
                                                                if (callStatus === 4 && smsStatus > 0) return <GreenStopIcon />;
                                                                if (smsStatus > 0) return <BlueCircle />;
                                                                if (callStatus === 4) return <GreenCircle />;

                                                                // Fallback icon mapping
                                                                return {
                                                                    3: <GrayCircle />, // idle
                                                                    7: <RedCircle />, // lock
                                                                    10: <RedStopIcon />, // block
                                                                    9: <RedDashedOutlineCircle />, // sim error
                                                                    0: <GrayDashedOutlineCircle />, // nosim
                                                                    11: <GreyOutlineCircle />, // sim inserted
                                                                    2: <GreenOutlineCircle />, // registering
                                                                    6: <RedOutlineCircle />, // reg failed
                                                                    15: <GreenDashedOutlineCircle />, // Access Data
                                                                }[callStatus] || '?';
                                                            })()}
                                                            {callState ? (
                                                                <>
                                                                    <Box
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: '50%',
                                                                            left: '50%',
                                                                            transform: 'translate(-50%, -50%)',
                                                                            width: 5,
                                                                            height: 5,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: '#14141a',
                                                                            boxShadow: '0 0 0 1px rgba(255,255,255,0.08)',
                                                                        }}
                                                                    />
                                                                    <Box
                                                                        sx={{
                                                                            position: 'absolute',
                                                                            top: '50%',
                                                                            left: '50%',
                                                                            transform: 'translate(-50%, -50%)',
                                                                            width: 5,
                                                                            height: 5,
                                                                            borderRadius: '50%',
                                                                            backgroundColor: callState.color,
                                                                            boxShadow: '0 0 0 1px rgba(0,0,0,0.45)',
                                                                        }}
                                                                    />
                                                                </>
                                                            ) : null}
                                                        </Box>
                                                    </Tooltip>
                                                    <Box sx={{ textAlign: 'left' }}>
                                                        <Typography sx={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
                                                            {timerDisplay}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            {/* Expiry / Balance */}
                                            <TableCell
                                                sx={{
                                                    fontSize: 13,
                                                    color: 'text.primary',
                                                    width: 23,
                                                    overflow: 'hidden',
                                                }}
                                                align="center"
                                                style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
                                            >
                                                {(() => {
                                                    const expiryTimestamp =
                                                        expiryData[`goip${goipNumber}`] &&
                                                        expiryData[`goip${goipNumber}`][index]
                                                            ? expiryData[`goip${goipNumber}`][index]
                                                            : null;
                                                    const balMeta = getBalProgressMeta(expiryTimestamp);
                                                    const isLoadingExpiry = loadingByNumber[`goip${goipNumber}-${index}`];
                                                    const remainingText =
                                                        expiryTimestamp && !isNaN(new Date(expiryTimestamp).getTime())
                                                            ? formatTimeDifference(expiryTimestamp)
                                                            : '--';

                                                    return (
                                                <Tooltip
                                                    title={
                                                        item[1] === ''
                                                            ? 'N/A'
                                                            : isLoadingExpiry ||
                                                                !expiryData[`goip${goipNumber}`] ||
                                                                !expiryData[`goip${goipNumber}`][index]
                                                                ? 'Fetching expiry...'
                                                                : isNaN(new Date(expiryData[`goip${goipNumber}`][index]).getTime())
                                                                    ? 'Invalid Date'
                                                                    : `${new Date(expiryData[`goip${goipNumber}`][index]).toLocaleString()} | Remaining: ${remainingText} | Status: ${balMeta.label}`
                                                    }
                                                    aria-label="expiry-date"
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'inline-flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minWidth: 0,
                                                            px: 0,
                                                            py: 0,
                                                            borderRadius: 0,
                                                            backgroundColor: 'transparent',
                                                        }}
                                                    >
                                                        {isLoadingExpiry ? (
                                                            <CircularProgress size={11} />
                                                        ) : balMeta.expired ? (
                                                            <Box
                                                                sx={{
                                                                    position: 'relative',
                                                                    display: 'inline-flex',
                                                                    width: 14,
                                                                    height: 14,
                                                                }}
                                                            >
                                                                <CircularProgress
                                                                    variant="determinate"
                                                                    value={100}
                                                                    size={14}
                                                                    thickness={7}
                                                                    sx={{
                                                                        color: 'rgba(255,107,107,0.2)',
                                                                    }}
                                                                />
                                                                <CircularProgress
                                                                    variant="indeterminate"
                                                                    size={14}
                                                                    thickness={7}
                                                                    disableShrink
                                                                    sx={{
                                                                        color: '#ff6b6b',
                                                                        position: 'absolute',
                                                                        left: 0,
                                                                        top: 0,
                                                                        animationDirection: 'reverse',
                                                                        '& .MuiCircularProgress-svg': {
                                                                            animationDirection: 'reverse',
                                                                        },
                                                                        '& .MuiCircularProgress-circle': {
                                                                            strokeLinecap: 'round',
                                                                        },
                                                                    }}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Box
                                                                sx={{
                                                                    position: 'relative',
                                                                    display: 'inline-flex',
                                                                    width: 14,
                                                                    height: 14,
                                                                    animation: balMeta.animate ? 'flashing 1s infinite' : 'none',
                                                                }}
                                                            >
                                                                <CircularProgress
                                                                    variant="determinate"
                                                                    value={100}
                                                                    size={14}
                                                                    thickness={7}
                                                                    sx={{
                                                                        color: balMeta.expired ? 'rgba(255,107,107,0.35)' : 'rgba(255,255,255,0.14)',
                                                                    }}
                                                                />
                                                                <CircularProgress
                                                                    variant="determinate"
                                                                    value={item[1] === '' ? 0 : balMeta.percent}
                                                                    size={14}
                                                                    thickness={7}
                                                                    sx={{
                                                                        color: item[1] === '' ? 'rgba(255,255,255,0.2)' : balMeta.color,
                                                                        position: 'absolute',
                                                                        left: 0,
                                                                        top: 0,
                                                                        transition: 'color 250ms ease',
                                                                    }}
                                                                />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                                    );
                                                })()}
                                            </TableCell>

                                            {/* Snt (Sent OK) */}
                                            <TableCell
                                                sx={{
                                                    fontSize: 13,
                                                    color: 'text.primary',
                                                    backgroundColor:
                                                        sentOkCount >= 1
                                                            ? 'rgba(40, 56, 120, 0.6)'
                                                            : 'inherit',
                                                    width: 24,
                                                    overflow: 'hidden',
                                                }}
                                                align="center"
                                                style={{ paddingTop: 0, paddingBottom: 0, paddingLeft: 0, paddingRight: 0 }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: 23,
                                                        px: 0.35,
                                                        py: 0.25,
                                                        borderRadius: 999,
                                                        fontSize: 11,
                                                        fontWeight: 700,
                                                        border: '1px solid rgba(255,255,255,0.05)',
                                                        backgroundColor:
                                                            sentOkCount >= 1
                                                                ? 'rgba(255,255,255,0.08)'
                                                                : 'rgba(255,255,255,0.03)',
                                                        color:
                                                            sentOkCount >= 1
                                                                ? 'rgba(255,255,255,0.9)'
                                                                : 'inherit',
                                                    }}
                                                    >
                                                        <span
                                                            style={{
                                                                color:
                                                                    sentFailCount > 0
                                                                        ? 'rgba(255,255,255,0.78)'
                                                                        : 'inherit',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {sentFailCount}
                                                        </span>
                                                    <span style={{ opacity: 0.5, margin: '0 3px' }}>|</span>
                                                    <span
                                                        style={{
                                                            color:
                                                                sentOkCount >= 1
                                                                    ? 'rgba(255,255,255,0.9)'
                                                                    : 'inherit',
                                                        }}
                                                    >
                                                        {sentOkCount}
                                                    </span>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
            </ThemeProvider>
        );
    };



    const countStatuses = () => {
        [1, 2, 3, 4, 5, 6, 7, 8, 9].forEach(goipNumber => {
            smsStatusData[`goip${goipNumber}`].forEach(item => {
                const voipStatus = item[2];
                const smsStatus = item[9];

                // Count VOIP statuses
                switch (voipStatus) {
                    case 3:
                        statusCounts.voipIdle++;
                        break;
                    case 4:
                        statusCounts.calling++;
                        break;
                    case 7:
                        statusCounts.lock++;
                        break;
                    case 10:
                        statusCounts.block++;
                        break;
                    case 9:
                        statusCounts.simError++;
                        break;
                    case 0:
                        statusCounts.noSim++;
                        break;
                    case 11:
                        statusCounts.simInserted++;
                        break;
                    case 2:
                        statusCounts.registering++;
                        break;
                    case 6:
                        statusCounts.regFailed++;
                        break;
                    case 15:
                        statusCounts.mobileData++;
                        break;
                    // Add other cases if needed
                }

                // Count SMS statuses
                if (smsStatus > 0) {
                    statusCounts.sending++;
                } else if (smsStatus === 0) {
                    statusCounts.smsIdle++;
                }
                // '?' condition is not counted
            });
        });
    };

    countStatuses();


    const [selectedGateway, setSelectedGateway] = useState('All');



    const [selectedsmsGateway, setSelectedsmsGateway] = useState('All');

    // Filter the logs based on the selected gateway
    const filteredSmsLogs = selectedsmsGateway === 'All'
        ? smsLogs
        : smsLogs.filter(log => log.Gateway === selectedsmsGateway);



    return (

        <Box
            sx={{
                boxSizing: 'border-box',
                overflowX: 'hidden',
                background:
                    'radial-gradient(1200px 600px at 15% -10%, rgba(78, 118, 255, 0.28), transparent 60%),' +
                    'radial-gradient(900px 600px at 110% 10%, rgba(0, 200, 170, 0.22), transparent 55%),' +
                    'linear-gradient(180deg, #0b0b10 0%, #101018 45%, #0d0f12 100%)',
                color: '#e8e9ee',
                py: 0,
            }}
        >
            {showPopup && (
                <PopupNotification message={popupMessage} className={fadeOut ? 'fade-out' : ''} />
            )}

            <Container
                maxWidth={false}
                disableGutters
                sx={{ px: { xs: 1, sm: 1.5, md: 2 } }}
            >
                <Stack spacing={0.35}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 1.5,
                            borderRadius: 3,
                            background: 'rgba(18, 18, 22, 0.75)',
                            border: '1px solid rgba(255,255,255,0.06)',
                            backdropFilter: 'blur(6px)',
                        }}
                    >
                        <Stack spacing={1}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexWrap: 'nowrap',
                                    position: 'relative',
                                    width: '100%',
                                    minHeight: 42,
                                }}
                            >
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Tooltip title="Fetch GoipStats">
                                        <Switch
                                            checked={cacheEnabled}
                                            onChange={handleToggle}
                                            color="secondary"
                                            sx={{ transform: 'scale(0.85)' }}
                                        />
                                    </Tooltip>
                                </Box>
                                <Box
                                    sx={{
                                        flex: '0 1 auto',
                                        width: 'fit-content',
                                        display: 'flex',
                                        flexWrap: 'nowrap',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        mx: 'auto',
                                        gap: 0.5,
                                        overflowX: 'auto',
                                        overflowY: 'hidden',
                                        maxWidth: '100%',
                                    }}
                                >
                                    <Tooltip title="Call Idle">
                                        <div>
                                            <StatusCard label="Call Idle" value={statusCounts.voipIdle} icon={<GrayCircle />} />
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="Calling">
                                        <div>
                                            <StatusCard label="Calling" value={statusCounts.calling} icon={<GreenCircle />} color="#5ad27a" />
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="Locked">
                                        <div>
                                            <StatusCard label="Locked" value={statusCounts.lock} icon={<RedCircle />} color="#ff6b6b" />
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="Blocked">
                                        <div>
                                            <StatusCard label="Blocked" value={statusCounts.block} icon={<RedStopIcon />} color="#ff6b6b" />
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="Sim Error">
                                        <div>
                                            <StatusCard label="Sim Error" value={statusCounts.simError} icon={<RedDashedOutlineCircle />} color="#ffb84d" />
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="No Sim">
                                        <div>
                                            <StatusCard label="No Sim" value={statusCounts.noSim} icon={<GrayDashedOutlineCircle />} />
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="Sim Reg Failed">
                                        <div>
                                            <StatusCard label="Reg Failed" value={statusCounts.regFailed} icon={<RedOutlineCircle />} color="#ff6b6b" />
                                        </div>
                                    </Tooltip>
                                    <Tooltip title="Sending">
                                        <div>
                                            <StatusCard label="Sending" value={statusCounts.sending} icon={<BlueCircle />} color="#6aa9ff" />
                                        </div>
                                    </Tooltip>
                                </Box>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        right: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Tooltip title="Auto Fetch Expiry">
                                        <Switch
                                            checked={autoRefetchEnabled}
                                            onChange={handleToggleSwitch}
                                            color="primary"
                                            sx={{ transform: 'scale(0.85)' }}
                                        />
                                    </Tooltip>
                                </Box>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        color="success"
                                        sx={{
                                            height: 8,
                                            borderRadius: 8,
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                        }}
                                        value={(statusCounts.calling / (statusCounts.calling + statusCounts.voipIdle)) * 100}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 160 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        sx={{
                                            height: 8,
                                            borderRadius: 8,
                                            backgroundColor: 'rgba(255,255,255,0.08)',
                                        }}
                                        value={(statusCounts.sending / (statusCounts.sending + statusCounts.smsIdle)) * 100}
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </Paper>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                    <TableContainer
                        sx={{
                            width: '100%',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            textAlign: 'center',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'inline-flex',
                                flexWrap: 'nowrap',
                                gap: 1.5,
                                width: 'max-content',
                                justifyContent: 'flex-start',
                                alignItems: 'start',
                                pb: 0.5,
                            }}
                        >
                            {activeGoips.slice().sort((a, b) => a - b).map(goipNumber => (
                                <Box key={goipNumber} sx={{ width: 'auto', minWidth: 'max-content', flex: '0 0 auto' }}>
                                    {data[`goip${goipNumber}`] && smsStatusData[`goip${goipNumber}`] && renderTable(goipNumber)}
                                </Box>
                            ))}
                        </Box>
                    </TableContainer>
                </Stack>
            </Container>
        </Box>
    );

};

export default DataTable;
